// Alert rules as the console reads them (plan step 3.5; Console D's #/alerts): each preset's
// parameters with the API's bounds (packages/accounts alerts/presets.ts), a rule's one-line
// summary and its condition as code, what it measures against its threshold (APS-303's
// last_value), and an event in a few words. Shared by Alerts, a rule's page, Settings ›
// Destinations, Agents and tokens, and Home.
import type { AlertDestination, AlertEvent, AlertPreset, AlertRule } from '../api/types'
import type { StatusKey } from '../components/d'
import type { IconName } from '../components/icons'
import type { MessageId, Translator } from '../i18n'
import { closeness, closestPair } from './slo'

export const PRESETS: AlertPreset[] = [
  'crash_free_below',
  'new_issue',
  'issue_regressed',
  'issue_spike',
  'endpoint_failure_above',
  'metric_change',
  'slo',
  'store_crash_rate',
  'store_anr_rate',
]

/** A parameter picked from a list (metric_change's metric and direction); the first is the default. */
export interface Choice {
  key: string
  label: MessageId
  options: { value: string; label: MessageId }[]
  /** Sent as a number (an SLO's window days, its slow ticket switch). */
  numeric?: boolean
}

/** Each preset's list parameters (alerts/presets.ts metric_change and slo, D119). */
export const CHOICES: Partial<Record<AlertPreset, Choice[]>> = {
  slo: [
    {
      key: 'window_days',
      label: 'settings.alerts.f.budgetWindow',
      numeric: true,
      options: [
        { value: '28', label: 'settings.alerts.window.28' },
        { value: '7', label: 'settings.alerts.window.7' },
        { value: '30', label: 'settings.alerts.window.30' },
      ],
    },
    {
      key: 'slow_ticket',
      label: 'settings.alerts.f.slowTicket',
      numeric: true,
      options: [
        { value: '0', label: 'settings.alerts.slowTicket.off' },
        { value: '1', label: 'settings.alerts.slowTicket.on' },
      ],
    },
  ],
  metric_change: [
    {
      key: 'metric',
      label: 'settings.alerts.f.metric',
      options: [
        { value: 'crash_rate', label: 'settings.alerts.metric.crash_rate' },
        { value: 'endpoint_failure_rate', label: 'settings.alerts.metric.endpoint_failure_rate' },
      ],
    },
    {
      key: 'direction',
      label: 'settings.alerts.f.direction',
      options: [
        { value: 'rises', label: 'settings.alerts.direction.rises' },
        { value: 'falls', label: 'settings.alerts.direction.falls' },
      ],
    },
  ],
}

export interface Field {
  key: string
  label: MessageId
  default?: number
  min: number
  max: number
  step: number
}

/** Google Play's thresholds (D62, alerts/presets.ts storeFields): user-days over 28 days. */
const storeFields = (overall: number): Field[] => [
  {
    key: 'threshold_percent',
    label: 'settings.alerts.f.threshold',
    default: overall,
    min: 0.01,
    max: 100,
    step: 0.01,
  },
  {
    key: 'device_threshold_percent',
    label: 'settings.alerts.f.deviceThreshold',
    default: 8,
    min: 0,
    max: 100,
    step: 0.1,
  },
  { key: 'window_days', label: 'settings.alerts.f.days', default: 28, min: 1, max: 28, step: 1 },
  {
    key: 'min_users',
    label: 'settings.alerts.f.minUserDays',
    default: 500,
    min: 10,
    max: 100_000_000,
    step: 1,
  },
  {
    key: 'min_device_users',
    label: 'settings.alerts.f.minDeviceUserDays',
    default: 100,
    min: 10,
    max: 100_000_000,
    step: 1,
  },
]

/** Each preset's numeric parameters, with the API's bounds (alerts/presets.ts). */
export const FIELDS: Record<AlertPreset, Field[]> = {
  crash_free_below: [
    {
      key: 'threshold_percent',
      label: 'settings.alerts.f.threshold',
      default: 99.5,
      min: 50,
      max: 99.99,
      step: 0.01,
    },
    {
      key: 'window_hours',
      label: 'settings.alerts.f.hours',
      default: 24,
      min: 1,
      max: 168,
      step: 1,
    },
    {
      key: 'min_sessions',
      label: 'settings.alerts.f.minSessions',
      default: 100,
      min: 10,
      max: 10_000_000,
      step: 1,
    },
  ],
  new_issue: [
    {
      key: 'min_occurrences',
      label: 'settings.alerts.f.minOccurrences',
      default: 1,
      min: 1,
      max: 10_000,
      step: 1,
    },
    {
      key: 'min_users',
      label: 'settings.alerts.f.minUsers',
      default: 0,
      min: 0,
      max: 10_000_000,
      step: 1,
    },
  ],
  // One message per regression the reopen job records (D100): nothing to tune.
  issue_regressed: [],
  issue_spike: [
    { key: 'factor', label: 'settings.alerts.f.factor', default: 5, min: 2, max: 1000, step: 0.5 },
    {
      key: 'min_occurrences',
      label: 'settings.alerts.f.minOccurrences',
      default: 20,
      min: 5,
      max: 10_000_000,
      step: 1,
    },
  ],
  endpoint_failure_above: [
    {
      key: 'threshold_percent',
      label: 'settings.alerts.f.threshold',
      default: 5,
      min: 0.1,
      max: 100,
      step: 0.1,
    },
    { key: 'window_hours', label: 'settings.alerts.f.hours', default: 1, min: 1, max: 24, step: 1 },
    {
      key: 'min_requests',
      label: 'settings.alerts.f.minRequests',
      default: 100,
      min: 20,
      max: 100_000_000,
      step: 1,
    },
  ],
  // The last window_hours against the window_hours before (D119).
  metric_change: [
    {
      key: 'change_percent',
      label: 'settings.alerts.f.change',
      default: 50,
      min: 0.1,
      max: 1000,
      step: 0.1,
    },
    {
      key: 'window_hours',
      label: 'settings.alerts.f.hours',
      default: 24,
      min: 1,
      max: 168,
      step: 1,
    },
    {
      key: 'min_volume',
      label: 'settings.alerts.f.minVolume',
      default: 100,
      min: 10,
      max: 100_000_000,
      step: 1,
    },
    {
      key: 'min_events',
      label: 'settings.alerts.f.minEvents',
      default: 10,
      min: 1,
      max: 100_000_000,
      step: 1,
    },
  ],
  // D119's SLO: the objective and the two alert rates (the windows are fixed per pair), when it
  // is at risk, and the sessions a window needs to be measured (APS-279 contract v1).
  slo: [
    {
      key: 'objective',
      label: 'settings.alerts.f.objective',
      default: 99.5,
      min: 90,
      max: 99.99,
      step: 0.01,
    },
    {
      key: 'page_rate',
      label: 'settings.alerts.f.pageRate',
      default: 14.4,
      min: 1,
      max: 100,
      step: 0.1,
    },
    {
      key: 'ticket_rate',
      label: 'settings.alerts.f.ticketRate',
      default: 6,
      min: 1,
      max: 100,
      step: 0.1,
    },
    {
      key: 'at_risk_below',
      label: 'settings.alerts.f.atRiskBelow',
      default: 50,
      min: 1,
      max: 99,
      step: 1,
    },
    {
      key: 'min_sessions',
      label: 'settings.alerts.f.minSessions',
      default: 100,
      min: 10,
      max: 10_000_000,
      step: 1,
    },
  ],
  store_crash_rate: storeFields(1.09),
  store_anr_rate: storeFields(0.47),
}

// Console D's order for an SLO: the objective defines the rule, the slow ticket sits by the ticket.
const PARAM_ORDER: Partial<Record<AlertPreset, string[]>> = {
  slo: [
    'objective',
    'window_days',
    'page_rate',
    'ticket_rate',
    'slow_ticket',
    'at_risk_below',
    'min_sessions',
  ],
}

/** One of a preset's parameters: a choice or a field. */
export type Param = { choice: Choice; field?: undefined } | { field: Field; choice?: undefined }

/** A preset's parameters in the order the form and the rule page show them. */
export function paramOrder(preset: AlertPreset): Param[] {
  const all: Param[] = [
    ...(CHOICES[preset] ?? []).map((choice) => ({ choice })),
    ...FIELDS[preset].map((field) => ({ field })),
  ]
  const order = PARAM_ORDER[preset]
  if (!order) return all
  const at = (x: Param) => order.indexOf((x.choice ?? x.field).key)
  return [...all].sort((a, b) => at(a) - at(b))
}

/** What the API refuses across fields: an SLO pages above the rate it tickets at. */
export function paramsValid(preset: AlertPreset, params: Record<string, unknown>): boolean {
  if (preset === 'slo') return num(params.page_rate) > num(params.ticket_rate)
  return true
}

/** Presets that take an optional release (metric_change: the crash rate only). */
export const HAS_RELEASE: AlertPreset[] = ['crash_free_below', 'new_issue', 'metric_change', 'slo']
/** Whether this rule's parameters take a release: metric_change's endpoint failures do not. */
export const takesRelease = (preset: AlertPreset, params: Record<string, unknown>) =>
  HAS_RELEASE.includes(preset) &&
  !(preset === 'metric_change' && params.metric === 'endpoint_failure_rate')
/**
 * How often the evaluator checks a preset (packages/accounts alerts/evaluator.ts): rate of change
 * every five minutes (CHANGE_EVERY_MS, D119), Google Play's rates hourly (STORE_EVERY_MS), the
 * rest every minute.
 */
export function checkedEvery(preset: AlertPreset): 'minute' | 'five' | 'hour' {
  if (preset === 'metric_change') return 'five'
  if (preset === 'store_crash_rate' || preset === 'store_anr_rate') return 'hour'
  return 'minute'
}
/** Presets on the whole project: the API takes no app for them. */
export const NO_APP: AlertPreset[] = ['issue_regressed']
/** What a GitHub or Linear destination takes (D44): a new crash group, a crash-free regression. */
export const HANDOFF_PRESETS: AlertPreset[] = ['new_issue', 'crash_free_below']

export const KIND_LABEL: Record<AlertDestination['kind'], MessageId> = {
  slack: 'settings.alerts.kind.slack',
  webhook: 'settings.alerts.kind.webhook',
  github: 'settings.alerts.kind.github',
  linear: 'settings.alerts.kind.linear',
  pagerduty: 'settings.alerts.kind.pagerduty',
  email: 'settings.alerts.kind.email',
}

/** A destination's icon: Slack's, GitHub's, the handoff's, an envelope, or a bolt. */
export function destinationIcon(d: Pick<AlertDestination, 'kind'>): IconName {
  switch (d.kind) {
    case 'slack':
      return 'slack'
    case 'github':
      return 'git'
    case 'linear':
      return 'agent'
    case 'email':
      return 'mail'
    default:
      return 'bolt'
  }
}

/** A PagerDuty Events API v2 integration key, as the API checks it. */
export const ROUTING_KEY = /^[A-Za-z0-9]{32}$/

export const PRESET_LABEL: Record<AlertPreset, MessageId> = {
  crash_free_below: 'settings.alerts.preset.crash_free_below',
  new_issue: 'settings.alerts.preset.new_issue',
  issue_regressed: 'settings.alerts.preset.issue_regressed',
  issue_spike: 'settings.alerts.preset.issue_spike',
  endpoint_failure_above: 'settings.alerts.preset.endpoint_failure_above',
  metric_change: 'settings.alerts.preset.metric_change',
  slo: 'settings.alerts.preset.slo',
  store_crash_rate: 'settings.alerts.preset.store_crash_rate',
  store_anr_rate: 'settings.alerts.preset.store_anr_rate',
}

export const num = (v: unknown) => (typeof v === 'number' ? v : Number(v ?? 0))
const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined)

/** One line saying what a rule watches. */
export function ruleSummary(
  rule: Pick<AlertRule, 'preset' | 'params'>,
  { t, number }: Pick<Translator, 't' | 'number'>,
): string {
  const p = rule.params
  // A threshold as the conditions write it: 99.50 %, 5.0 %, 1.09 %.
  const pct = (v: unknown) =>
    number(num(v), { minimumFractionDigits: num(v) >= 90 ? 2 : 1, maximumFractionDigits: 2 })
  const release = str(p.release)
  const base = (() => {
    switch (rule.preset) {
      case 'crash_free_below':
        return t('settings.alerts.summary.crash_free_below', {
          threshold: pct(p.threshold_percent),
          hours: num(p.window_hours),
        })
      case 'new_issue':
        return t('settings.alerts.summary.new_issue', { min: num(p.min_occurrences) })
      case 'issue_regressed':
        return t('settings.alerts.summary.issue_regressed')
      case 'issue_spike':
        return t('settings.alerts.summary.issue_spike', {
          factor: number(num(p.factor)),
          min: num(p.min_occurrences),
        })
      case 'endpoint_failure_above':
        return t('settings.alerts.summary.endpoint_failure_above', {
          threshold: pct(p.threshold_percent),
          hours: num(p.window_hours),
        })
      case 'metric_change':
        return t('settings.alerts.summary.metric_change', {
          metric: p.metric === 'endpoint_failure_rate' ? 'endpoint' : 'crash',
          direction: p.direction === 'falls' ? 'falls' : 'rises',
          change: number(num(p.change_percent), { maximumFractionDigits: 1 }),
          hours: num(p.window_hours),
        })
      case 'slo':
        return t('settings.alerts.summary.slo', {
          objective: pct(p.objective),
          days: num(p.window_days),
        })
      case 'store_crash_rate':
      case 'store_anr_rate': {
        const values = {
          threshold: pct(p.threshold_percent),
          device: pct(p.device_threshold_percent),
          days: num(p.window_days),
        }
        const crash = rule.preset === 'store_crash_rate'
        return num(p.device_threshold_percent) > 0
          ? t(
              crash
                ? 'settings.alerts.summary.store_crash_rate'
                : 'settings.alerts.summary.store_anr_rate',
              values,
            )
          : t(
              crash
                ? 'settings.alerts.summary.store_crash_rate_overall'
                : 'settings.alerts.summary.store_anr_rate_overall',
              values,
            )
      }
    }
  })()
  return release ? t('settings.alerts.summary.inRelease', { rule: base, release }) : base
}

/**
 * A rule's condition as code (the mock's `crash_free_sessions < 99.50% over 24h`): telemetry
 * names and numbers, never translated. Built from the rule's own parameters.
 */
export function ruleCondition(rule: Pick<AlertRule, 'preset' | 'params'>): string {
  const p = rule.params
  // A threshold as the mock writes it: 99.50 %, 5.0 % (the second decimal near 100 %).
  const pct = (v: unknown) => `${num(v).toFixed(num(v) >= 90 ? 2 : 1)} %`
  const release = str(p.release)
  const on = release ? ` on ${release}` : ''
  const endpoint =
    str(p.method) && str(p.host) ? ` on ${p.method} ${p.host}${str(p.template) ?? ''}` : ''
  switch (rule.preset) {
    case 'crash_free_below':
      return `crash_free_sessions < ${pct(p.threshold_percent)} over ${num(p.window_hours)}h, min ${num(p.min_sessions)} sessions${on}`
    case 'new_issue':
      return `new_issue.occurrences >= ${num(p.min_occurrences)}${num(p.min_users) > 0 ? `, users >= ${num(p.min_users)}` : ''}${on}`
    case 'issue_regressed':
      return 'issue.regressed: a resolved issue seen in a later release'
    case 'issue_spike':
      return `issue.occurrences_1h > ${num(p.factor)}x hourly_mean, min ${num(p.min_occurrences)}`
    case 'endpoint_failure_above':
      return `endpoint.failure_rate > ${pct(p.threshold_percent)} over ${num(p.window_hours)}h, min ${num(p.min_requests)} requests${endpoint}`
    case 'metric_change': {
      const failures = p.metric === 'endpoint_failure_rate'
      const metric = failures ? 'endpoint.failure_rate' : 'crash_rate'
      const unit = failures ? 'requests' : 'sessions'
      const events = num(p.min_events)
      const bad = failures
        ? events === 1
          ? 'failure'
          : 'failures'
        : events === 1
          ? 'crash'
          : 'crashes'
      const hours = num(p.window_hours)
      return `pct_change(${metric}, ${hours}h vs previous ${hours}h) ${p.direction === 'falls' ? '<= -' : '>= +'}${num(p.change_percent)}%, min ${num(p.min_volume)} ${unit}, ${events} ${bad}${on}`
    }
    case 'slo': {
      // The mock's `crash_free_sessions >= 99.50% over 28d · burn_rate 14.4x (1h, 5m) page, ...`.
      const slow = num(p.slow_ticket) === 1 ? ', 1x (3d, 6h) ticket' : ''
      return `crash_free_sessions >= ${pct(p.objective)} over ${num(p.window_days)}d · burn_rate ${num(p.page_rate)}x (1h, 5m) page, ${num(p.ticket_rate)}x (6h, 30m) ticket${slow}${on}`
    }
    case 'store_crash_rate':
    case 'store_anr_rate': {
      const name = rule.preset === 'store_crash_rate' ? 'play.crash_rate' : 'play.anr_rate'
      const device =
        num(p.device_threshold_percent) > 0
          ? ` or >= ${pct(p.device_threshold_percent)} on one device`
          : ''
      return `${name} >= ${pct(p.threshold_percent)}${device} over ${num(p.window_days)}d, min ${num(p.min_users)} user-days`
    }
  }
}

/** What a rule measures and where it fires, in the unit of `last_value`. */
export interface Measure {
  /** percent: a change in percent of the window before (metric_change). */
  unit: 'ratio' | 'factor' | 'percent'
  threshold: number
  firesWhen: 'below' | 'above' | 'at_or_below' | 'at_or_above'
}

/** Whether a measure fires on the low side (a fall, crash-free under its threshold). */
export const firesLow = (m: Pick<Measure, 'firesWhen'>) =>
  m.firesWhen === 'below' || m.firesWhen === 'at_or_below'

/** A rule's threshold in the unit the evaluator measures; none for the issue presets. */
export function measureOf(rule: Pick<AlertRule, 'preset' | 'params'>): Measure | undefined {
  const p = rule.params
  switch (rule.preset) {
    case 'crash_free_below':
      return { unit: 'ratio', threshold: num(p.threshold_percent) / 100, firesWhen: 'below' }
    case 'endpoint_failure_above':
    case 'store_crash_rate':
    case 'store_anr_rate':
      return { unit: 'ratio', threshold: num(p.threshold_percent) / 100, firesWhen: 'above' }
    case 'issue_spike':
      return { unit: 'factor', threshold: num(p.factor), firesWhen: 'above' }
    case 'metric_change': {
      const falls = p.direction === 'falls'
      const change = num(p.change_percent)
      return {
        unit: 'percent',
        threshold: falls ? -change : change,
        firesWhen: falls ? 'at_or_below' : 'at_or_above',
      }
    }
    default:
      return undefined
  }
}

/** A measured value as the mock writes it: 99.38 %, 3.1 %, 3.2×. */
export function formatMeasure(
  i18n: Pick<Translator, 'number'>,
  value: number,
  unit: Measure['unit'],
): string {
  if (unit === 'factor') return `${i18n.number(value, { maximumFractionDigits: 1 })}×`
  if (unit === 'percent')
    return `${value > 0 ? '+' : ''}${i18n.number(value, { maximumFractionDigits: 1 })} %`
  const pct = value * 100
  // Near 100 % (crash-free) and under 1 % the second decimal is the one that moves.
  const digits = pct >= 90 || pct < 1 ? 2 : 1
  return `${i18n.number(pct, { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`
}

/**
 * How far a rule's last value is from firing, in its unit (percentage points for a rate), and
 * that distance against the threshold, for ordering "closest to firing". Undefined without a
 * measure or a value. An SLO reads its closest pair (APS-279 contract v1): now is that pair's
 * long-window burn, the threshold its rate (the mock's "1.3× · 14.4× · 13.1×").
 */
export function headroom(
  rule: Pick<AlertRule, 'preset' | 'params' | 'last_value'> & Partial<Pick<AlertRule, 'slo'>>,
): { value: number; share: number; measure: Measure; now?: number } | undefined {
  if (rule.preset === 'slo') {
    const p = rule.slo ? closestPair(rule.slo) : undefined
    const c = p ? closeness(p) : null
    if (!p || c === null || p.burn_long === null) return undefined
    const measure: Measure = { unit: 'factor', threshold: p.rate, firesWhen: 'above' }
    return { value: p.rate - p.burn_long, share: 1 - c, measure, now: p.burn_long }
  }
  const m = measureOf(rule)
  if (!m || rule.last_value === null || rule.last_value === undefined) return undefined
  const value = firesLow(m) ? rule.last_value - m.threshold : m.threshold - rule.last_value
  // For crash-free the room is what is left of the allowed failure (1 − threshold); for a change,
  // the change it fires at.
  const scale =
    m.unit === 'percent' ? Math.abs(m.threshold) : firesLow(m) ? 1 - m.threshold : m.threshold
  return { value, share: scale > 0 ? value / scale : value, measure: m }
}

/** A headroom as the mock writes it: 1.9 pts for a rate, 1.8× for a spike. */
export function formatHeadroom(
  i18n: Pick<Translator, 'number'>,
  h: NonNullable<ReturnType<typeof headroom>>,
): string {
  if (h.measure.unit === 'factor') return `${i18n.number(h.value, { maximumFractionDigits: 1 })}×`
  // A change's room is already in percent.
  const points = h.measure.unit === 'percent' ? h.value : h.value * 100
  return `${i18n.number(points, { maximumFractionDigits: 2 })} pts`
}

/** A rule's state as D's status: off, muted until, firing, at risk (an SLO) or ok. */
export function ruleStatus(
  r: Pick<AlertRule, 'enabled' | 'muted_until' | 'state'> & {
    paused_by_plan?: boolean
    ticket_muted_until?: string | null
    slo?: AlertRule['slo']
  },
  now: number,
  { t, date }: Pick<Translator, 't' | 'date'>,
): {
  s: StatusKey
  label: string
  key: 'off' | 'paused' | 'muted' | 'firing' | 'at_risk' | 'ok'
} {
  if (!r.enabled) return { s: 'mute', label: t('settings.alerts.state.off'), key: 'off' }
  // Not evaluated on a plan without its preset (D119): its stored state is from before.
  if (r.paused_by_plan)
    return { s: 'mute', label: t('settings.alerts.state.paused'), key: 'paused' }
  const running = (u: string | null | undefined): u is string => !!u && Date.parse(u) > now
  // An SLO is muted only when its mutes cover every channel that fires (slo.muted, contract v2):
  // a ticket-only mute leaves a firing page in Firing, still paging. The API reads it with its
  // own clock, so a mute must also still run on this one (one that ends between reads stops
  // reading muted at once). The later running mute is shown.
  const covered = r.slo
    ? r.slo.muted && (running(r.muted_until) || running(r.ticket_muted_until))
    : running(r.muted_until)
  const until = covered
    ? [r.muted_until, r.ticket_muted_until].filter(running).sort().at(-1)
    : undefined
  if (until)
    return {
      s: 'mute',
      label: t('settings.alerts.state.muted', {
        date: date(until, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      }),
      key: 'muted',
    }
  if (r.state === 'firing')
    return { s: 'fire', label: t('settings.alerts.state.firing'), key: 'firing' }
  if (r.state === 'at_risk')
    return { s: 'reg', label: t('settings.alerts.state.atRisk'), key: 'at_risk' }
  return { s: 'on', label: t('settings.alerts.state.ok'), key: 'ok' }
}

/** What one event was, in a few words. */
export function eventSummary(
  e: AlertEvent,
  { t, number }: Pick<Translator, 't' | 'number'>,
): string {
  const d = e.detail
  switch (e.kind) {
    case 'test':
      return t('settings.alerts.event.test')
    case 'suppressed':
      return t('settings.alerts.event.suppressed', {
        window: d.window === 'day' ? 'day' : 'minute',
      })
    case 'new_issue':
      return t('settings.alerts.event.new_issue', { occurrences: num(d.occurrences) })
    case 'issue_regressed':
      return str(d.fixed_in)
        ? t('settings.alerts.event.issue_regressedFixed', { fixed: str(d.fixed_in) ?? '' })
        : t('settings.alerts.event.issue_regressed')
    case 'issue_resolved':
      return t('settings.alerts.event.issue_resolved', {
        status: d.status === 'ignored' ? 'ignored' : 'resolved',
      })
    case 'at_risk':
    case 'at_risk_cleared':
      return t(
        e.kind === 'at_risk'
          ? 'settings.alerts.event.at_risk'
          : 'settings.alerts.event.at_risk_cleared',
        { left: number(Math.max(0, num(d.budget_left)) * 100, { maximumFractionDigits: 0 }) },
      )
    case 'fired':
    case 'resolved': {
      // An SLO pair's burn over its long window (APS-279 contract v1: detail.pair, burn_long, long).
      if (str(d.pair)) {
        const burn = `${number(num(d.burn_long), { maximumFractionDigits: 2 })}×`
        return t(
          e.kind === 'fired'
            ? 'settings.alerts.event.sloFired'
            : 'settings.alerts.event.sloResolved',
          { severity: e.severity === 'page' ? 'page' : 'ticket', burn, window: str(d.long) ?? '' },
        )
      }
      const value =
        e.value === null
          ? '—'
          : typeof d.last_hour === 'number'
            ? `${number(Math.round(e.value))}×`
            : formatMeasure({ number }, e.value, 'ratio')
      return t(
        e.kind === 'fired' ? 'settings.alerts.event.fired' : 'settings.alerts.event.resolved',
        { value },
      )
    }
  }
}

/** A delivery as D's status: sent, queued or sending, failed, held back. */
export const DELIVERY: Record<AlertEvent['delivery'], { s: StatusKey; label: MessageId }> = {
  sent: { s: 'res', label: 'settings.alerts.delivery.sent' },
  pending: { s: 'on', label: 'settings.alerts.delivery.pending' },
  sending: { s: 'on', label: 'settings.alerts.delivery.sending' },
  failed: { s: 'fire', label: 'settings.alerts.delivery.failed' },
  suppressed: { s: 'mute', label: 'settings.alerts.delivery.suppressed' },
}

/** The issue an event is about, when it names one (a new issue, a spike, a regression). */
export const eventIssue = (e: AlertEvent): string | undefined => str(e.detail.fingerprint)
